import React, { useState, useEffect } from 'react';

const defaultStyles = {
  position: 'relative' as const,
  overflow: 'hidden',
  cursor: 'pointer',
  display: 'block',
  float: 'left' as const,
};

interface Props {
  className?: string;
  edit?: boolean;
  half?: boolean;
  value: number;
  count?: number;
  char?: string;
  size?: number;
  color1?: string;
  color2?: string;
  onChange?: (avgValue: number) => void;
}

export const ReactStars: React.FC<Props> = ({
  className,
  edit = true,
  half = true,
  value,
  count = 5,
  char = '★',
  size = 15,
  color1 = 'gray',
  color2 = '#ffd700',
  onChange,
}) => {
  const [uniqueness] = useState((Math.random() + '').replace('.', ''));
  const [avgValue, setAvgValue] = useState(value || 0);
  const [stars, setStars] = useState([{ active: false }]);
  const [halfStar, setHalfStar] = useState({
    at: Math.floor(value),
    hidden: half && value % 1 < 0.5,
  });

  useEffect(() => {
    setStars(getStars(avgValue));
  }, []);

  useEffect(() => {
    if (onChange) {
      onChange(avgValue);
    }
  }, [avgValue]);

  useEffect(() => {
    setStars(getStars(avgValue));
    setAvgValue(value);
    setHalfStar({
      at: Math.floor(avgValue),
      hidden: half && value % 1 < 0.5,
    });
  }, [value]);

  const getHalfStarStyles = (colorValue: string, uniqValue: string) => {
    return `
        .react-stars-${uniqValue}:before {
        position: absolute;
        overflow: hidden;
        display: block;
        z-index: 1;
        top: 0; left: 0;
        width: 50%;
        content: attr(data-forhalf);
        color: ${colorValue};
    }`;
  };

  const isDecimal = (val: number) => {
    return val % 1 !== 0;
  };

  const getRate = () => {
    let starsValue;
    if (half) {
      starsValue = Math.floor(avgValue);
    } else {
      starsValue = Math.round(avgValue);
    }
    return starsValue;
  };

  const getStars = (activeCount?: number) => {
    if (typeof activeCount === 'undefined') {
      activeCount = getRate();
    }
    const starsValue = [];
    for (let i = 0; i < count; i++) {
      starsValue.push({
        active: i <= activeCount - 1,
      });
    }
    return starsValue;
  };

  const mouseOver = (event: any) => {
    if (!edit) {
      return;
    }
    let index = Number(event.target.getAttribute('data-index'));
    if (half) {
      const isAtHalf = moreThanHalf(event, size);
      halfStar.hidden = isAtHalf;
      if (isAtHalf) {
        index = index + 1;
      }
      halfStar.at = index;
    } else {
      index = index + 1;
    }
    setStars(getStars(index));
  };

  const moreThanHalf = (event: any, sizeValue: number) => {
    const { target } = event;
    let mouseAt = event.clientX - target.getBoundingClientRect().left;
    mouseAt = Math.round(Math.abs(mouseAt));
    return mouseAt > sizeValue / 2;
  };

  const mouseLeave = () => {
    if (!edit) {
      return;
    }
    if (half) {
      halfStar.hidden = !isDecimal(avgValue);
      halfStar.at = Math.floor(avgValue);
    }
    setStars(getStars());
  };

  const clicked = (event: any) => {
    if (!edit) {
      return;
    }
    let index = Number(event.target.getAttribute('data-index'));
    let clickedValue;
    if (half) {
      const isAtHalf = moreThanHalf(event, size);
      halfStar.hidden = isAtHalf;
      if (isAtHalf) {
        index = index + 1;
      }
      clickedValue = isAtHalf ? index : index + 0.5;
      halfStar.at = index;
    } else {
      clickedValue = index = index + 1;
    }
    setAvgValue(clickedValue);
    setStars(getStars(index));
  };

  const renderHalfStarStyleElement = () => {
    return <style dangerouslySetInnerHTML={{ __html: getHalfStarStyles(color2, uniqueness) }} />;
  };

  const renderStars = () => {
    return stars.map((star, i) => {
      let starClass = '';
      const newStyles = {
        color: star.active ? color2 : color1,
        cursor: edit ? 'pointer' : 'default',
        fontSize: `${size}px`,
      };
      const style = Object.assign({}, defaultStyles, newStyles);

      if (half && !halfStar.hidden && halfStar.at === i) {
        starClass = `react-stars-${uniqueness}`;
      }

      return (
        <span
          className={starClass}
          style={style}
          key={i}
          data-index={i}
          data-forhalf={char}
          onMouseOver={mouseOver}
          onMouseMove={mouseOver}
          onMouseLeave={mouseLeave}
          onClick={clicked}
        >
          {char}
        </span>
      );
    });
  };

  return (
    <div className={className}>
      {half ? renderHalfStarStyleElement() : null}
      {renderStars()}
    </div>
  );
};
